/**
 * packages/ui-shell/src/services/LicenseService.ts
 * Gumroad / Lemon Squeezy ライセンスキー認証およびローカル永続化サービス
 */

export interface StoredLicenseData {
  key: string;
  productPermalink: string;
  activatedAt: number;
  email?: string;
  uses?: number;
}

export type LicenseErrorCode =
  | 'empty_key'
  | 'invalid_key'
  | 'max_uses_exceeded'
  | 'network_error'
  | 'refunded'
  | 'unknown';

export interface LicenseVerifySuccess {
  success: true;
  message: string;
  data: StoredLicenseData;
}

export interface LicenseVerifyFailure {
  success: false;
  error: LicenseErrorCode;
  message: string;
}

export type LicenseVerifyResult = LicenseVerifySuccess | LicenseVerifyFailure;

export type LicenseChangeListener = (isPro: boolean, data: StoredLicenseData | null) => void;

export class LicenseService {
  private static instance: LicenseService | null = null;
  private readonly STORAGE_KEY = 'purin_license_v1';

  // デフォルトの Gumroad 設定 (後から設定変更可能)
  private productPermalink = 'jigglytouch';
  private productId = 'tnEfst7nNVG43TlCtbhaRA==';
  private gumroadBuyUrl = 'https://kilroysan.gumroad.com/l/jigglytouch';

  private listeners: Set<LicenseChangeListener> = new Set();

  constructor() {
    // グローバル設定が事前定義されている場合の自動読み込み
    if (typeof window !== 'undefined') {
      const winConfig = (window as unknown as { __PURIN_LICENSE_CONFIG__?: { productPermalink?: string; productId?: string; buyUrl?: string } })
        .__PURIN_LICENSE_CONFIG__;
      if (winConfig?.productPermalink) {
        this.productPermalink = winConfig.productPermalink;
      }
      if (winConfig?.productId) {
        this.productId = winConfig.productId;
      }
      if (winConfig?.buyUrl) {
        this.gumroadBuyUrl = winConfig.buyUrl;
      }
    }
  }

  static getInstance(): LicenseService {
    if (!LicenseService.instance) {
      LicenseService.instance = new LicenseService();
    }
    return LicenseService.instance;
  }

  /**
   * 商品パーマリンク、購入URL、商品IDの設定
   */
  setProductConfig(permalink: string, buyUrl?: string, productId?: string): void {
    this.productPermalink = permalink;
    if (buyUrl) {
      this.gumroadBuyUrl = buyUrl;
    }
    if (productId) {
      this.productId = productId;
    }
  }

  getProductPermalink(): string {
    return this.productPermalink;
  }

  getProductId(): string {
    return this.productId;
  }

  getBuyUrl(): string {
    return this.gumroadBuyUrl;
  }

  /**
   * ライセンス変更リスナーの購読
   */
  subscribe(listener: LicenseChangeListener): () => void {
    this.listeners.add(listener);
    listener(this.isProActivated(), this.getStoredLicense());
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const isPro = this.isProActivated();
    const data = this.getStoredLicense();
    this.listeners.forEach((listener) => listener(isPro, data));
  }

  /**
   * ローカルストレージに保存されたライセンス情報の取得
   */
  getStoredLicense(): StoredLicenseData | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredLicenseData;
      if (!parsed.key) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Pro版が有効化されているかどうか
   */
  isProActivated(): boolean {
    const stored = this.getStoredLicense();
    return stored !== null && !!stored.key;
  }

  /**
   * Gumroad API を使用したライセンスキーの認証と有効化
   */
  async verifyAndActivate(key: string): Promise<LicenseVerifyResult> {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      return {
        success: false,
        error: 'empty_key',
        message: 'License key is empty.',
      };
    }

    // 開発・デモ用バイパスキー (オフラインや検証環境用)
    if (trimmedKey === 'JIGGLY-PRO-DEMO-2026' || trimmedKey === 'PURIN-PRO-DEMO-2026') {
      const demoData: StoredLicenseData = {
        key: trimmedKey,
        productPermalink: this.productPermalink,
        activatedAt: Date.now(),
        email: 'demo@jigglytouch.app',
        uses: 1,
      };
      this.saveLicense(demoData);
      this.notify();
      return {
        success: true,
        message: 'Demo Pro License activated successfully!',
        data: demoData,
      };
    }

    try {
      const params = new URLSearchParams();
      if (this.productId) {
        params.append('product_id', this.productId);
      }
      if (this.productPermalink) {
        params.append('product_permalink', this.productPermalink);
      }
      params.append('license_key', trimmedKey);
      params.append('increment_uses_count', 'false');

      const response = await fetch('https://api.gumroad.com/v2/licenses/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const json = await response.json().catch(() => null);

      if (response.ok && json && json.success === true) {
        // 返金やチャージバックの確認
        if (json.purchase?.refunded || json.purchase?.chargebacked) {
          return {
            success: false,
            error: 'refunded',
            message: 'This license has been refunded or cancelled.',
          };
        }

        const data: StoredLicenseData = {
          key: trimmedKey,
          productPermalink: this.productPermalink,
          activatedAt: Date.now(),
          email: json.purchase?.email,
          uses: json.uses,
        };

        this.saveLicense(data);
        this.notify();

        return {
          success: true,
          message: 'License verified and activated successfully!',
          data,
        };
      }

      // エラー理由の解析
      const errorMsg: string = json?.message || '';
      const lower = errorMsg.toLowerCase();

      if (lower.includes('maximum') || lower.includes('uses') || lower.includes('limit')) {
        return {
          success: false,
          error: 'max_uses_exceeded',
          message: 'This license key has reached its maximum device limit.',
        };
      }

      return {
        success: false,
        error: 'invalid_key',
        message: errorMsg || 'Invalid license key.',
      };
    } catch (err) {
      console.error('[LicenseService] Failed to verify license with Gumroad API:', err);
      return {
        success: false,
        error: 'network_error',
        message: 'Network error connecting to verification API.',
      };
    }
  }

  /**
   * ライセンス情報をローカルストレージへ保存
   */
  private saveLicense(data: StoredLicenseData): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('[LicenseService] Failed to save license to localStorage:', e);
    }
  }

  /**
   * 現在の端末のライセンスを解除（無料版に戻す）
   */
  deactivate(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      this.notify();
    } catch (e) {
      console.warn('[LicenseService] Failed to remove license from localStorage:', e);
    }
  }
}
